// Markets — unified catalog workspace (post-migration-0014).
//
// One page, two panes. Left: tabbed catalog browser — three flat,
// searchable lists (Categories / Events / Markets) each with their
// own "+ New" affordance. Right: detail panel that swaps shape based
// on the selected entity (category → events + direct markets, event
// → markets, market → outcomes + resolve/cancel controls). Replaces
// the prior Miller-column workspace, the per-entity list pages, and
// the dedicated /markets/rules/new + /markets/events/new wizards —
// admins can do everything from this single screen.
//
// Lifecycle model after migration 0014:
//   * is_published is an independent on/off toggle on every node.
//     Categories / events are visible to public when published; markets
//     are exposed to the engine only when published.
//   * Markets carry a 3-stage status: OPEN | RESOLVED | CANCELED.
//     Resolve folds the prior close + resolve + settle into one click.
//
// URL state: ?tab= (which list is open) and ?selected= (which node
// the right pane shows). Refresh and deep-links preserve both.
// Filter/search/sort state on the list tabs stays in-memory for now —
// promote to URL state once operators ask for sharable filtered views.
import { useEffect, useState, useTransition } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  CatalogBrowser,
  type CatalogTab,
} from "@/components/markets/CatalogBrowser";
import { type SelectedNode } from "@/components/markets/selection";
import { CategoryDetailPanel } from "@/components/markets/CategoryDetailPanel";
import { EventDetailPanel } from "@/components/markets/EventDetailPanel";
import { MarketDetailPanel } from "@/components/markets/MarketDetailPanel";
import { CommandPalette } from "@/components/markets/CommandPalette";

// Search params are all optional so existing <Link to="/markets"> sites
// (orders rows, nav) don't have to pass anything; defaults apply in
// MarketsPage. validateSearch normalizes unknown tab values to undefined
// so a stale or hand-typed value falls back to the Categories default.
interface MarketsSearch {
  tab?: CatalogTab;
  // Wire format: "category:<id>" | "event:<id>" | "market:<id>". Stored
  // as a single string so the URL stays compact.
  selected?: string;
}

const VALID_TABS: CatalogTab[] = ["categories", "events", "markets"];

function parseSelected(raw: string | undefined): SelectedNode {
  if (!raw) return null;
  const [kind, id] = raw.split(":", 2);
  if (!id) return null;
  if (kind === "category" || kind === "event" || kind === "market") {
    return { kind, id };
  }
  return null;
}

function serializeSelected(n: SelectedNode): string | undefined {
  if (!n) return undefined;
  return `${n.kind}:${n.id}`;
}

export const Route = createFileRoute("/_app/markets/")({
  validateSearch: (search: Record<string, unknown>): MarketsSearch => {
    const tabRaw = typeof search.tab === "string" ? search.tab : "";
    const tab = VALID_TABS.includes(tabRaw as CatalogTab)
      ? (tabRaw as CatalogTab)
      : undefined;
    return {
      tab,
      selected:
        typeof search.selected === "string" ? search.selected : undefined,
    };
  },
  component: MarketsPage,
});

function MarketsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [, startTransition] = useTransition();

  // selectedNav drives any in-rail highlight (tree row, list row) and
  // updates urgently so a click feels instant. selectedDetail drives
  // the right-pane queries via the URL — wrapped in startTransition
  // so the (sometimes heavy) detail re-render doesn't block the
  // highlight commit.
  const [selectedNav, setSelectedNav] = useState<SelectedNode>(
    parseSelected(search.selected),
  );
  const selectedDetail = parseSelected(search.selected);
  // Sync the nav highlight to the URL when navigation happens from
  // outside this component (back/forward button, deep-link paste).
  useEffect(() => {
    setSelectedNav(parseSelected(search.selected));
  }, [search.selected]);
  const setSelected = (next: SelectedNode) => {
    setSelectedNav(next);
    startTransition(() => {
      navigate({
        search: (prev) => ({ ...prev, selected: serializeSelected(next) }),
        replace: true,
      });
    });
  };

  const setTab = (next: CatalogTab) => {
    navigate({
      search: (prev) => ({ ...prev, tab: next }),
      replace: true,
    });
  };

  const [paletteOpen, setPaletteOpen] = useState(false);
  const activeTab: CatalogTab = search.tab ?? "categories";

  // Cmd+K / Ctrl+K opens the global catalog search. Bound on the
  // /markets page only for now — promote to _app.tsx later if other
  // pages want the same shortcut. We intentionally skip the bind when
  // focus is inside an editable element where Cmd+K may already mean
  // something else (e.g. browser quick-find inside a code editor).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div
      // h-full fills the content box of .admin-main-content (set up
      // by the parent flex chain in app.css: .admin-shell 100vh →
      // .admin-main flex:1 → .admin-main-content flex:1 min-h-0).
      // A calc() with a guessed chrome offset was off by ~28px and
      // caused the page to scroll vertically by a few pixels.
      className="grid h-full gap-4 grid-cols-[28rem_1fr]"
    >
      <aside className="overflow-hidden rounded-md border border-line-1 bg-bg-1">
        <CatalogBrowser
          tab={activeTab}
          onTabChange={setTab}
          selected={selectedNav}
          onSelect={setSelected}
        />
      </aside>

      <main className="overflow-hidden rounded-md border border-line-1 bg-bg-1">
        {selectedDetail === null ? (
          <EmptyDetail />
        ) : selectedDetail.kind === "category" ? (
          <CategoryDetailPanel
            categoryID={selectedDetail.id}
            onSelect={setSelected}
            onDeleted={() => setSelected(null)}
          />
        ) : selectedDetail.kind === "event" ? (
          <EventDetailPanel
            eventID={selectedDetail.id}
            onSelect={setSelected}
            onDeleted={() => setSelected(null)}
          />
        ) : (
          <MarketDetailPanel
            marketID={selectedDetail.id}
            onSelect={setSelected}
            onDeleted={() => setSelected(null)}
          />
        )}
      </main>

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onSelect={(n) => {
          // Selecting from the palette swaps to the matching list tab
          // so the highlight in the left rail lines up with what the
          // detail pane is showing.
          if (n) {
            const tabFor: Record<"category" | "event" | "market", CatalogTab> = {
              category: "categories",
              event: "events",
              market: "markets",
            };
            const next = tabFor[n.kind];
            navigate({
              search: (prev) => ({
                ...prev,
                tab: next,
                selected: serializeSelected(n),
              }),
              replace: true,
            });
            setSelectedNav(n);
            return;
          }
          setSelected(n);
        }}
      />
    </div>
  );
}

function EmptyDetail() {
  return (
    <div className="flex h-full items-center justify-center px-6 text-center">
      <div className="max-w-sm">
        <h2 className="text-base font-semibold text-t-1">Pick something to start</h2>
        <p className="mt-2 text-xs text-muted">
          Use the Categories, Events, or Markets tabs on the left to browse
          and edit. Each tab has a{" "}
          <span className="font-medium text-t-2">+ New</span> button for
          creating a fresh row. Press{" "}
          <kbd className="rounded border border-line-1 bg-bg-2 px-1 py-0.5 text-3xs text-t-2">
            ⌘K
          </kbd>{" "}
          to jump directly to any market, event, or category by name.
        </p>
      </div>
    </div>
  );
}
